// web/src/pages/onboarding.tsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useLang } from '@/hooks/use-lang'
import { t } from '@/lib/i18n'
import { Wordmark } from '@/components/wordmark'
import { TEST_URLS } from '@/lib/test-urls'
import { inputStyle } from '@/lib/ui'

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {Array.from({ length: total }, (_, i) => {
        const step = i + 1
        const isCompleted = step < current
        const isCurrent = step === current
        return (
          <div key={step} className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300"
              style={
                isCurrent
                  ? { backgroundColor: 'var(--accent)', color: '#fff' }
                  : isCompleted
                  ? { backgroundColor: 'color-mix(in srgb, var(--green) 18%, transparent)', color: 'var(--green)', border: '1px solid color-mix(in srgb, var(--green) 25%, transparent)' }
                  : { backgroundColor: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }
              }
            >
              {isCompleted ? (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                step
              )}
            </div>
            {i < total - 1 && (
              <div
                className="w-8 h-px transition-all duration-300"
                style={{ backgroundColor: isCompleted ? 'color-mix(in srgb, var(--green) 32%, transparent)' : 'var(--border)' }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

export function OnboardingPage() {
  useLang()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [step, setStep] = useState(1)
  const [workspaceName, setWorkspaceName] = useState('')
  const [endpointName, setEndpointName] = useState('')
  const [targetUrl, setTargetUrl] = useState('')
  const [createdEndpoint, setCreatedEndpoint] = useState<{ name: string; slug: string } | null>(null)

  const patchTenant = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch('/api/tenant', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant'] }),
  })

  const createEndpoint = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ name: string; slug: string }>('/api/endpoints', body),
  })

  const handleStep1 = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!workspaceName.trim()) return
    await patchTenant.mutateAsync({ name: workspaceName.trim() })
    setStep(2)
  }

  const handleStep2 = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!endpointName.trim() || !targetUrl.trim()) return
    const result = await createEndpoint.mutateAsync({
      name: endpointName.trim(),
      target_url: targetUrl.trim(),
      provider: 'generic',
    })
    setCreatedEndpoint({ name: result.name, slug: result.slug })
    queryClient.invalidateQueries({ queryKey: ['endpoints'] })
    setStep(3)
  }

  const handleFinish = async () => {
    await patchTenant.mutateAsync({ settings: { onboarding_completed: true } })
    navigate('/', { replace: true })
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--bg)' }}
    >
      <div className="w-full max-w-md">
        {/* Logo / brand */}
        <div className="text-center mb-8">
          <img src="/logo.png" className="w-10 h-10 mb-3" alt="WebhookLens" />
          <p className="text-xs font-medium" style={{ color: 'var(--muted)' }}><Wordmark /></p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-8"
          style={{ border: '1px solid var(--border)', backgroundColor: 'var(--surface)', boxShadow: '0 8px 32px #00000060' }}
        >
          <StepIndicator current={step} total={3} />

          {step === 1 && (
            <form onSubmit={handleStep1}>
              <h1 className="text-xl font-bold mb-1" style={{ color: 'var(--white)' }}>{t('onboarding_step_1_title')}</h1>
              <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>{t('onboarding_step_1_desc')}</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text)' }}>
                    {t('workspace_name')}
                  </label>
                  <input
                    type="text"
                    value={workspaceName}
                    onChange={e => setWorkspaceName(e.target.value)}
                    placeholder="Acme Inc."
                    required
                    autoFocus
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none focus:ring-1 focus:ring-[var(--accent)] transition"
                    style={inputStyle}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={!workspaceName.trim() || patchTenant.isPending}
                className="mt-6 w-full py-2.5 rounded-lg text-sm font-semibold text-[var(--white)] disabled:opacity-50 transition-all"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                {patchTenant.isPending ? t('loading') : t('next')}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleStep2}>
              <h1 className="text-xl font-bold mb-1" style={{ color: 'var(--white)' }}>{t('onboarding_step_2_title')}</h1>
              <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>{t('onboarding_step_2_desc')}</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text)' }}>
                    {t('endpoint_name')}
                  </label>
                  <input
                    type="text"
                    value={endpointName}
                    onChange={e => setEndpointName(e.target.value)}
                    placeholder="My Stripe Webhook"
                    required
                    autoFocus
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none focus:ring-1 focus:ring-[var(--accent)] transition"
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text)' }}>
                    {t('target_url')}
                  </label>
                  <input
                    type="url"
                    value={targetUrl}
                    onChange={e => setTargetUrl(e.target.value)}
                    placeholder="https://httpbin.org/post"
                    required
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none focus:ring-1 focus:ring-[var(--accent)] transition"
                    style={inputStyle}
                  />
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>{t('try_urls')}:</span>
                    {TEST_URLS.map(({ label, url }) => (
                      <button
                        key={url}
                        type="button"
                        onClick={() => setTargetUrl(url)}
                        className="text-xs px-2 py-0.5 rounded-full transition hover:opacity-80"
                        style={{ backgroundColor: 'var(--surface-2)', color: 'var(--accent)', border: '1px solid var(--border)' }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {createEndpoint.isError && (
                <p className="mt-3 text-xs" style={{ color: 'var(--red)' }}>
                  {t('onboardingCreateFailed')}
                </p>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-2.5 rounded-lg text-sm font-medium transition"
                  style={{ backgroundColor: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }}
                >
                  {t('back')}
                </button>
                <button
                  type="submit"
                  disabled={!endpointName.trim() || !targetUrl.trim() || createEndpoint.isPending}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-[var(--white)] disabled:opacity-50 transition-all"
                  style={{ backgroundColor: 'var(--accent)' }}
                >
                  {createEndpoint.isPending ? t('loading') : t('next')}
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div>
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
                style={{ backgroundColor: 'var(--accent-soft)', border: '1px solid color-mix(in srgb, var(--green) 25%, transparent)' }}
              >
                <svg className="w-6 h-6" style={{ color: 'var(--green)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h1 className="text-xl font-bold mb-1" style={{ color: 'var(--white)' }}>{t('onboarding_step_3_title')}</h1>
              <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>{t('onboarding_step_3_desc')}</p>

              {createdEndpoint && (
                <div
                  className="rounded-xl p-4 mb-6 space-y-2"
                  style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)' }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>{t('workspace_name')}</span>
                    <span className="text-xs font-medium" style={{ color: 'var(--white)' }}>{workspaceName}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>{t('endpoint_name')}</span>
                    <span className="text-xs font-medium" style={{ color: 'var(--white)' }}>{createdEndpoint.name}</span>
                  </div>
                </div>
              )}

              <button
                onClick={handleFinish}
                disabled={patchTenant.isPending}
                className="w-full py-2.5 rounded-lg text-sm font-semibold text-[var(--white)] disabled:opacity-50 transition-all"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                {patchTenant.isPending ? t('loading') : t('finish')}
              </button>

              <div className="mt-3 text-center">
                <button
                  onClick={() => { setStep(2); setEndpointName(''); setTargetUrl(''); setCreatedEndpoint(null) }}
                  className="text-xs transition"
                  style={{ color: 'var(--muted)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--text)' }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--muted)' }}
                >
                  {t('create_another_endpoint')} →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Step text */}
        <p className="text-center mt-4 text-xs" style={{ color: 'var(--muted)' }}>
          {t('helpStep')} {step} / 3
        </p>
      </div>
    </div>
  )
}
